/**
 * Input Change Handler Helpers for CreateBlankModal
 * Extracted to reduce cognitive complexity of handleInputChange function
 */

import { isEndDateInPast } from "./validationHelpers";

// Handle client name selection and auto-fill email
export const handleClientNameChange = (value, clients, setEmails, setFormData, setErrors) => {
  const selectedClient = Array.isArray(clients)
    ? clients.find((client) => String(client.client_id) === value)
    : null;
  const clientEmails = [selectedClient?.clientEmail].filter(Boolean);
  setEmails(clientEmails);
  setFormData((prev) => ({
    ...prev,
    clientName: value,
    clientEmail: clientEmails[0] || "",
  }));
  setErrors((prev) => ({ ...prev, clientName: "" }));
};

// Handle vendor name selection and auto-fill email
export const handleVendorNameChange = (value, vendors, setVendorEmails, setFormData, setErrors) => {
  const selectedVendor = Array.isArray(vendors)
    ? vendors.find((vendor) => String(vendor.vendor_id) === value)
    : null;
  const vendorEmails = [selectedVendor?.vendorEmail].filter(Boolean);
  setVendorEmails(vendorEmails);
  setFormData((prev) => ({
    ...prev,
    vendorName: value,
    vendorEmail: vendorEmails[0] || "",
  }));
  setErrors((prev) => ({ ...prev, vendorName: "" }));
};

// Handle PO number field
export const handlePoNumberChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, po_number: value }));
};

// Handle net term field
export const handleNetTermChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, net_term: value }));
};

// Handle bill rate currency field
export const handleBillRateCurrencyChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, bill_rate_currency: value }));
};

// Handle billing country field
export const handleBillingCountryChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, billing_country: value }));
};

// Handle termination date field
export const handleTerminationDateChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, termination_date: value }));
};

// Handle termination reason field
export const handleTerminationReasonChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, termination_reason: value }));
};

// Handle practices field
export const handlePracticesChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, practices: value }));
};

// Handle billing type field
export const handleBillingTypeChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, billing_type: value }));
};

// Handle owner department field with console logging
export const handleOwnerDepartmentChange = (value, setFormData) => {
  setFormData((prev) => ({ ...prev, ownerDepartment: value }));
  console.log("Selected ownerDepartment:", value);
};

// Handle generic field change
export const handleGenericFieldChange = (name, value, setFormData) => {
  setFormData((prev) => ({ ...prev, [name]: value }));
};

// Clear error for any changed field
export const clearFieldError = (name, setErrors) => {
  setErrors((prev) => ({ ...prev, [name]: "" }));
};

/**
 * Main handler that routes to appropriate field handler
 * Reduces cognitive complexity by using a dispatch pattern
 */
export const createInputChangeHandler = (
  clients,
  vendors,
  setEmails,
  setVendorEmails,
  setFormData,
  setErrors
) => {
  return (e) => {
    const { name, value } = e.target;

    // Dispatch to specific handlers based on field name
    switch (name) {
      case "clientName":
        handleClientNameChange(value, clients, setEmails, setFormData, setErrors);
        break;
      case "vendorName":
        handleVendorNameChange(value, vendors, setVendorEmails, setFormData, setErrors);
        break;
      case "po_number":
        handlePoNumberChange(value, setFormData);
        break;
      case "net_term":
        handleNetTermChange(value, setFormData);
        break;
      case "bill_rate_currency":
        handleBillRateCurrencyChange(value, setFormData);
        break;
      case "billing_country":
        handleBillingCountryChange(value, setFormData);
        break;
      case "termination_date":
        handleTerminationDateChange(value, setFormData);
        break;
      case "termination_reason":
        handleTerminationReasonChange(value, setFormData);
        break;
      case "practices":
        handlePracticesChange(value, setFormData);
        break;
      case "billing_type":
        handleBillingTypeChange(value, setFormData);
        break;
      case "ownerDepartment":
        handleOwnerDepartmentChange(value, setFormData);
        break;
      case "endDate":
        setFormData((prev) => {
          const isPast = isEndDateInPast(value, false);
          return {
            ...prev,
            endDate: value,
            ...(isPast ? { renewalDate: "" } : {})
          };
        });
        if (isEndDateInPast(value, false)) {
          setErrors((prev) => ({ ...prev, endDate: "", renewalDate: "" }));
        } else {
          clearFieldError("endDate", setErrors);
        }
        return;
      default:
        handleGenericFieldChange(name, value, setFormData);
    }

    // Clear error for changed field
    clearFieldError(name, setErrors);
  };
};
